import {useCallback, useRef, useState} from 'react'
import {Button, Form} from 'react-bootstrap'
import {PersonalLeadOneLead} from '../leadTypes'
import {
  AddressComponent,
  GooglePlacesAutocompleteInput,
  PlaceSelection,
} from '../../../components/GooglePlacesAutocompleteInput'
import {AddressSearchModal} from '../../../components/AddressSearchModal'

export const FormPersonalLead = ({info}: {info?: PersonalLeadOneLead | null}) => {
  const cityRef = useRef<HTMLInputElement | null>(null)
  const countryRef = useRef<HTMLInputElement | null>(null)
  const stateRef = useRef<HTMLInputElement | null>(null)
  const postalCodeRef = useRef<HTMLInputElement | null>(null)
  const streetAddressRef = useRef<HTMLInputElement | null>(null)
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false)
  const [modalInitialAddress, setModalInitialAddress] = useState<string>('')

  const getAddressPart = useCallback(
    (components: AddressComponent[] | undefined, types: string[]) => {
      if (!components || components.length === 0) return ''
      const component = components.find((item) => types.every((type) => item.types.includes(type)))
      return component?.long_name ?? ''
    },
    []
  )

  const handlePlaceSelect = useCallback(
    ({addressComponents, address}: PlaceSelection) => {
      if (address && streetAddressRef.current) {
        streetAddressRef.current.value = address
      }

      if (!addressComponents) return

      const city =
        getAddressPart(addressComponents, ['locality']) ||
        getAddressPart(addressComponents, ['postal_town']) ||
        getAddressPart(addressComponents, ['administrative_area_level_2'])
      const state = getAddressPart(addressComponents, ['administrative_area_level_1'])
      const country = getAddressPart(addressComponents, ['country'])
      const postalCode = getAddressPart(addressComponents, ['postal_code'])

      if (cityRef.current) cityRef.current.value = city || ''
      if (stateRef.current) stateRef.current.value = state || ''
      if (countryRef.current) countryRef.current.value = country || ''
      if (postalCodeRef.current) postalCodeRef.current.value = postalCode || ''
    },
    [getAddressPart]
  )

  const openAddressModal = useCallback(() => {
    const currentValue = streetAddressRef.current?.value ?? info?.streetAddress ?? ''
    setModalInitialAddress(currentValue)
    setIsAddressModalOpen(true)
  }, [info?.streetAddress])

  const closeAddressModal = useCallback(() => {
    setIsAddressModalOpen(false)
  }, [])

  return (
    <>
      <Form.Group className='mb-3'>
        <Form.Label>Street Address</Form.Label>
        <GooglePlacesAutocompleteInput
          name='streetAddress'
          type='text'
          placeholder='Street Address'
          defaultValue={info?.streetAddress || undefined}
          onPlaceSelect={handlePlaceSelect}
          ref={streetAddressRef}
        />
        <Button
          variant='primary'
          size='sm'
          className='mt-2'
          type='button'
          onClick={openAddressModal}
        >
          Search Address
        </Button>
      </Form.Group>
      <Form.Group className='mb-3'>
        <Form.Label>City</Form.Label>
        <Form.Control
          name='city'
          type='text'
          placeholder='City'
          defaultValue={info?.city || undefined}
          ref={cityRef}
        />
      </Form.Group>
      <Form.Group className='mb-3'>
        <Form.Label>State</Form.Label>
        <Form.Control
          type='text'
          name='state'
          placeholder='State'
          defaultValue={info?.state || undefined}
          ref={stateRef}
        />
      </Form.Group>
      <Form.Group className='mb-3'>
        <Form.Label>Zip Code</Form.Label>
        <Form.Control
          name='postalCode'
          type='text'
          placeholder='Zip Code'
          defaultValue={info?.postalCode || undefined}
          ref={postalCodeRef}
        />
      </Form.Group>
      {/* Hidden Country field - filled by address search */}
      <input
        type='hidden'
        name='country'
        defaultValue={info?.country || undefined}
        ref={countryRef}
      />

      <AddressSearchModal
        show={isAddressModalOpen}
        onClose={closeAddressModal}
        onSelect={handlePlaceSelect}
        initialAddress={modalInitialAddress}
        modalTitle='Search Address'
      />
    </>
  )
}
